import {
  CSV_TARGETS,
  IDENT_KEYS,
  isoDateOf,
  uomFactors,
  type ParsedCsv,
} from "./csv";

export type PointResultRow = {
  sample_id: string;
  properties: Record<string, unknown>;
  raw: Record<string, string | null>;
};

/*
 * One CSV row's mapped properties. Zones and point layers both post this bag.
 * isoDateOf, not the raw cell: "9/ 2/2026" 500'd in a date bind after the
 * browser's own id check had already passed.
 * A "<col> UOM" neighbor that says lb is multiplied by 0.5 once here. A header
 * such as K_lb_ac is only a name, so it is not multiplied.
 */
export function mappedResultProperties(
  cells: string[],
  mapping: Record<string, number | null>,
  factors: Record<string, number>,
): { properties: Record<string, unknown>; hasValue: boolean } {
  const properties: Record<string, unknown> = {};
  let hasValue = false;
  for (const target of CSV_TARGETS) {
    if (
      target.key === "lat" ||
      target.key === "lon" ||
      target.key === "point_id"
    ) {
      continue;
    }
    const column = mapping[target.key];
    if (column == null) continue;
    const raw = (cells[column] ?? "").trim();
    if (!raw) continue;
    const numeric = Number(raw);
    properties[target.key] =
      target.key === "sampled_at"
        ? isoDateOf(raw)
        : Number.isFinite(numeric)
          ? numeric * (factors[target.key] ?? 1)
          : raw;
    if (!IDENT_KEYS.has(target.key)) hasValue = true;
  }
  return { properties, hasValue };
}

function rawCells(
  headers: string[],
  cells: string[],
): Record<string, string | null> {
  const raw: Record<string, string | null> = {};
  headers.forEach((header, index) => {
    const name = header.trim() || `column ${index + 1}`;
    const cell = (cells[index] ?? "").trim();
    raw[name] = cell === "" ? null : cell;
  });
  return raw;
}

function listed(ids: string[]): string {
  const shown = ids.slice(0, 12);
  const extra = ids.length > 12 ? ` … (+${ids.length - 12})` : "";
  return shown.join(", ") + extra;
}

/* Rows the point-layer preview already matched, keyed by the CSV sample id. */
export function buildPointResultRows(
  parsed: ParsedCsv,
  mapping: Record<string, number | null>,
): {
  rows: PointResultRow[];
  valuelessIds: string[];
  blankIds: string[];
} {
  const idColumn = mapping.point_id;
  const factors = uomFactors(parsed, mapping);
  const rows: PointResultRow[] = [];
  const valuelessIds: string[] = [];
  const blankIds: string[] = [];
  parsed.rows.forEach((cells, index) => {
    const sampleId = idColumn == null ? "" : (cells[idColumn] ?? "").trim();
    if (!sampleId) {
      blankIds.push(String(index + 1));
      return;
    }
    const built = mappedResultProperties(cells, mapping, factors);
    if (!built.hasValue) {
      valuelessIds.push(sampleId);
      return;
    }
    rows.push({
      sample_id: sampleId,
      properties: built.properties,
      raw: rawCells(parsed.headers, cells),
    });
  });
  return { rows, valuelessIds, blankIds };
}

export function pointRowProblem(built: {
  rows: PointResultRow[];
  valuelessIds: string[];
  blankIds: string[];
}): string | null {
  if (built.blankIds.length) {
    return `These rows have a blank ID: ${listed(built.blankIds)}. Nothing was attached.`;
  }
  if (built.valuelessIds.length) {
    return `These IDs have no recognised values: ${listed(built.valuelessIds)}. Nothing was attached.`;
  }
  if (!built.rows.length) {
    return "The file has no result rows. Nothing was attached.";
  }
  return null;
}
